import { createAction, createActionGroup, props } from '@ngrx/store';

import {
  DiagnosisExplanation,
  RemoteServiceChartConfig,
  RemoteServiceFeatures,
  RemoteServiceSetThresholdQueryParams,
  ViError,
} from '@vi-iot/client';
import { AnnualReportTemplate } from '@vi-iot/client/apis/annual-report/annual-report.model';

import { RemoteServiceOverlayTypeEnum } from './../../+installations/installation/installation-overlay.model';
import { RemoteServiceDiagnosisEntry } from './remote-service.model';
import { RichDevice } from '../devices';

export namespace RemoteServiceActions {
  export enum Type {
    SELECT_DIAGNOSIS_ENTRY = '[REMOTE SERVICE] Select Diagnosis Entry',
    SELECT_FIRST_DIAGNOSIS_ENTRY = '[REMOTE SERVICE] Select First Diagnosis Entry',
    SELECT_FIRST_ANOMALY_ENTRY = '[REMOTE SERVICE] Select First Anomaly Entry',
    LOAD_CHARTS_CONFIG = '[REMOTE SERVICE] Load Charts Config',
    LOAD_CHARTS_CONFIG_SUCCESS = '[REMOTE SERVICE] Load Chart Config Success',
    LOAD_CHARTS_CONFIG_ERROR = '[REMOTE SERVICE] Load Chart Config Error',
    LOAD_CHART_FEATURES = '[REMOTE SERVICE] Load Chart Features',
    LOAD_CHART_FEATURES_SUCCESS = '[REMOTE SERVICE] Load Chart Features Success',
    LOAD_CHART_FEATURES_ERROR = '[REMOTE SERVICE] Load Chart Features Error',
    LOAD_ALL_SUMMARY = '[REMOTE SERVICE] Load All Summary',
    LOAD_ALL_SUMMARY_SUCCESS = '[REMOTE SERVICE] Load All Summary Success',
    LOAD_ALL_SUMMARY_ERROR = '[REMOTE SERVICE] Load All Summary Error',
    LOAD_DIAGNOSIS = '[REMOTE SERVICE] Load Diagnosis Data',
    LOAD_DIAGNOSIS_SUCCESS = '[REMOTE SERVICE] Load Diagnosis Success',
    LOAD_DIAGNOSIS_ERROR = '[REMOTE SERVICE] Load Diagnosis Error',
    CREATE_ANNUAL_REPORT = '[REMOTE SERVICE] Create Annual Report',
    CREATE_ANNUAL_REPORT_SUCCESS = '[REMOTE SERVICE] Create Annual Report Success',
    CREATE_ANNUAL_REPORT_ERROR = '[REMOTE SERVICE] Create Annual Report Error',
    GET_ANNUAL_REPORT_URL = '[REMOTE SERVICE] Get Annual Report Url',
    GET_ANNUAL_REPORT_URL_SUCCESS = '[REMOTE SERVICE] Get Annual Report Url Success',
    GET_ANNUAL_REPORT_URL_ERROR = '[REMOTE SERVICE] Get Annual Report Url Error',
    GET_ANNUAL_REPORT = '[REMOTE SERVICE] Get Annual Report',
    CHANGE_TIMERANGE = '[REMOTE SERVICE] Change Timerange',
    SELECT_FEATURE = '[REMOTE SERVICE] Select Feature',
    SET_CHART_EXTREMES = '[REMOTE SERVICE] Set Chart Extremes',
    SAVE_TRESHOLD = '[REMOTE SERVICE] Save Threshold',
    SAVE_TRESHOLD_SUCCESS = '[REMOTE SERVICE] Save Threshold Success',
    SAVE_TRESHOLD_ERROR = '[REMOTE SERVICE] Save Threshold Error',
    CLEAR_DETAILED_INSPECTION_STATE = '[REMOTE SERVICE] Clear Detailed Inspection State',
    CLEAR_STATE = '[REMOTE SERVICE] Clear State',
    TAB_CHANGED = '[REMOTE SERVICE] Tab changed',
    GET_LOW_BUDGET_NOTIFICATION = '[REMOTE SERVICE] GET Low Budget Notification',
    HIDE_LOW_BUDGET_NOTIFICATION = '[REMOTE SERVICE] Hide Low Budget Notification',
  }

  export const selectDiagnosisEntry = createAction(
    Type.SELECT_DIAGNOSIS_ENTRY,
    props<{
      diagnosisEntry: RemoteServiceDiagnosisEntry;
    }>()
  );

  export const selectFirstDiagnosisEntry = createAction(Type.SELECT_FIRST_DIAGNOSIS_ENTRY);
  export const selectFirstAnomalyEntry = createAction(
    Type.SELECT_FIRST_ANOMALY_ENTRY,
    props<{ overwrite: boolean }>()
  );

  export const loadChartsConfig = createAction(Type.LOAD_CHARTS_CONFIG);

  export const setChartExtremes = createAction(
    Type.SET_CHART_EXTREMES,
    props<{
      startPointMs: number;
      endPointMs: number;
    }>()
  );

  export const loadChartsConfigSuccess = createAction(
    Type.LOAD_CHARTS_CONFIG_SUCCESS,
    props<{
      chartsConfig: RemoteServiceChartConfig[];
    }>()
  );

  export const loadChartsConfigError = createAction(
    Type.LOAD_CHARTS_CONFIG_ERROR,
    props<{
      error: any;
    }>()
  );

  export const loadAllSummary = createAction(
    Type.LOAD_ALL_SUMMARY,
    props<{
      boilerSerials: string[];
    }>()
  );

  export const loadAllSummarySuccess = createAction(
    Type.LOAD_ALL_SUMMARY_SUCCESS,
    props<{
      summary: any[];
    }>()
  );

  export const loadAllSummaryError = createAction(
    Type.LOAD_ALL_SUMMARY_ERROR,
    props<{
      error: any;
    }>()
  );

  export const loadDiagnosis = createAction(
    Type.LOAD_DIAGNOSIS,
    props<{
      deviceSerial: string;
    }>()
  );

  export const loadDiagnosisSuccess = createAction(
    Type.LOAD_DIAGNOSIS_SUCCESS,
    props<{
      diagnosisData: RemoteServiceDiagnosisEntry[];
    }>()
  );

  export const loadDiagnosisError = createAction(
    Type.LOAD_DIAGNOSIS_ERROR,
    props<{
      error: any;
    }>()
  );

  export const loadChartFeatures = createAction(
    Type.LOAD_CHART_FEATURES,
    props<{
      deviceSerial: string;
    }>()
  );

  export const loadChartFeaturesSuccess = createAction(
    Type.LOAD_CHART_FEATURES_SUCCESS,
    props<{
      chartsData: RemoteServiceFeatures;
    }>()
  );

  export const loadChartFeaturesError = createAction(
    Type.LOAD_CHART_FEATURES_ERROR,
    props<{
      error: any;
    }>()
  );

  export const saveThreshold = createAction(
    Type.SAVE_TRESHOLD,
    props<{ params: RemoteServiceSetThresholdQueryParams }>()
  );

  export const saveThresholdSuccess = createAction(
    Type.SAVE_TRESHOLD_SUCCESS,
    props<{ params: RemoteServiceSetThresholdQueryParams }>()
  );

  export const saveThresholdError = createAction(
    Type.SAVE_TRESHOLD_ERROR,
    props<{
      error: any;
    }>()
  );

  export const createAnnualReport = createAction(
    Type.CREATE_ANNUAL_REPORT,
    props<{
      reportTemplate: AnnualReportTemplate;
      deviceSerial: string;
      gatewaySerial: string;
    }>()
  );

  export const createAnnualReportSuccess = createAction(
    Type.CREATE_ANNUAL_REPORT_SUCCESS,
    props<{
      processId: string;
    }>()
  );

  export const createAnnualReportError = createAction(
    Type.CREATE_ANNUAL_REPORT_ERROR,
    props<{
      error: any;
    }>()
  );

  export const getAnnualReportUrl = createAction(Type.GET_ANNUAL_REPORT_URL);

  export const getAnnualReportUrlSuccess = createAction(
    Type.GET_ANNUAL_REPORT_URL_SUCCESS,
    props<{
      fileUri: string;
    }>()
  );

  export const getAnnualReportUrlError = createAction(
    Type.GET_ANNUAL_REPORT_URL_ERROR,
    props<{
      error: any;
    }>()
  );

  export const getAnnualReport = createAction(
    Type.GET_ANNUAL_REPORT,
    props<{
      reportTemplate: AnnualReportTemplate;
      gatewaySerial: string;
      deviceSerial: string;
    }>()
  );

  export const clearDetailedInspectionState = createAction(Type.CLEAR_DETAILED_INSPECTION_STATE);

  export const clearState = createAction(Type.CLEAR_STATE);

  export const tabChanged = createAction(
    Type.TAB_CHANGED,
    props<{
      tabType: RemoteServiceOverlayTypeEnum;
    }>()
  );

  export const getLowBudgetNotification = createAction(Type.GET_LOW_BUDGET_NOTIFICATION);

  export const hideLowBudgetNotification = createAction(Type.HIDE_LOW_BUDGET_NOTIFICATION);

  export const selectDevice = createAction(
    '[REMOTE SERVICE] Select device',
    props<{ device: RichDevice }>()
  );

  export const loadDiagnosisWhitelist = createActionGroup({
    source: 'REMOTE SERVICE Load Diagnosis Whitelist',
    events: {
      attempt: props<{ boilerId: string }>(),
      success: props<{ diagnoses: string[] }>(),
      error: props<{ error: ViError }>(),
    },
  });

  export const loadDiagnosisExplanation = createActionGroup({
    source: 'REMOTE SERVICE Load Diagnosis Explanation',
    events: {
      request: props<{
        diagnosisName: string;
        boilerId: string;
        lang: string;
        startDatetime: string;
        endDatetime: string;
      }>(),
      attempt: props<{
        diagnosisName: string;
        boilerId: string;
        lang: string;
        startDatetime: string;
        endDatetime: string;
      }>(),
      success: props<{ diagnosisName: string; explanation: DiagnosisExplanation }>(),
      error: props<{ diagnosisName: string; error: ViError }>(),
    },
  });
}
